import { trpc } from '@tencent/tpro_rmonitor_user_manager_user_role_auth';
import moment from 'moment';
import * as uuid from 'uuid';
import { getStaffName, getUserId, isWOA } from '@/infrastructure/auth';
import { TRPCGet, TRPCPost } from '@/infrastructure/axios';
import { API_PREFIX } from '@/infrastructure/axios/code';
import { logger } from '@/infrastructure/logger';
import { getProductId, getPid } from '@/infrastructure/product-info';
import { userService } from '@/service/user';
import { WithTypeCast } from '@raft/utils';
type IUser = WithTypeCast<trpc.rmonitor.user_manager.IUser>;
type IChangeUserReq = WithTypeCast<trpc.rmonitor.user_manager.IChangeUserReq>;
type IDeleteUserReq = WithTypeCast<trpc.rmonitor.user_manager.IDeleteUserReq>;
type IRole = WithTypeCast<trpc.rmonitor.user_manager.IRole>;
type IAddUserRsp = WithTypeCast<trpc.rmonitor.user_manager.IAddUserRsp>;
type IDeleteUserRsp = WithTypeCast<trpc.rmonitor.user_manager.IDeleteUserRsp>;
type IChangeUserRsp = WithTypeCast<trpc.rmonitor.user_manager.IChangeUserRsp>;
type ICheckUserExistRsp = WithTypeCast<trpc.rmonitor.user_manager.ICheckUserExistRsp>;
type IBasicUser = WithTypeCast<trpc.rmonitor.user_manager.IBasicUser>;
type IExpireTimeStrategie = WithTypeCast<trpc.rmonitor.user_manager.ExpireTimeStrategie>;
type ISetStrategieRsp = WithTypeCast<trpc.rmonitor.user_manager.ISetStrategieRsp>;

export interface User {
  id: number;
  name: string;
  role_id: number;
  department: string;
  create_time: number;
}
export interface UserItem {
  createTime: string;
  department: string;
  id: number;
  name: string;
  roleId: number;
  expireTime: number;
}

export interface RoleRsp {
  id: number;
  name: string;
}

export interface AddUserParams {
  name: string | string[];
  roleId: number;
  oldRoleId?: number;
  expireTime: number;
}

export interface AddApplyUserParams {
  name?: string | string[];
  roleId?: number;
  oldRoleId?: number;
  expireTime?: number;
  applyer?: string;
  userList?: IUser[]
}

export interface AutoApplyParams {
  strategies?: IExpireTimeStrategie[];
  departments?: string[];
}

export const EXPIRE_OPTIONS = [{ label: '1天', value: 1 },
  { label: '7天', value: 7 },
  { label: '15天', value: 15 },
  { label: '30天', value: 30 },
  { label: '90天', value: 90 },
  { label: '180天', value: 180 },
  { label: '360天', value: 360 }];

export const axiosRequestConfig = () => ({
  headers: {
    'Content-Type': 'application/json',
    'Server-Type': 'trpc',
    staffName: getUserId(),
    tenantKey: getProductId(),
  },
});

export const getRoleByUser = async (): Promise<boolean> => {
  let isAdmin = false;
  const productId = getProductId();

  if (!productId) {
    logger.info('getRoleByUser: No ProductId');
    return false;
  }

  if (!isWOA()) {
    isAdmin = await userService.checkIsAdmin({
      productId,
    });
  } else {
    try {
      const resp = await TRPCPost(
        `/${API_PREFIX}/trpc/trpc.rmonitor.user_manager.UserRoleAuth/GetRoleByUser`,
        {
          productId,
          username: getStaffName(),
        },
        axiosRequestConfig(),
      );
      isAdmin = resp.role.name === '管理员';
    } catch (e) {
      logger.error('GetRoleByUser err: ', e);
    }
  }

  return isAdmin;
};

export const getRoleByUserForPro = async (): Promise<boolean> => {
  try {
    const productId = getProductId();
    if (!productId) {
      logger.info('getRoleByUser: No ProductId');
      return false;
    }
    return await userService.checkIsAdmin({
      productId,
    });
  } catch (e) {
    logger.error('getRoleByUser err', e);
    return false;
  }
};

// 获取权限列表
export const getAllRoles = async () => {
  try {
    const resp = await TRPCGet(`/${API_PREFIX}/trpc/trpc.rmonitor.user_manager.UserRoleAuth/GetAllRoles?productId=${getProductId()}`, axiosRequestConfig());
    const data: IRole[] = resp.roles;
    return data;
  } catch (e) {
    logger.error('getAllRoles err', e);
    return [];
  }
};

export const getAllUsers = async () => {
  try {
    const resp = await TRPCPost(`/${API_PREFIX}/trpc/trpc.rmonitor.user_manager.UserRoleAuth/GetAllUsers`, {
      productId: getProductId(),
    },  axiosRequestConfig());
    const data = resp.users;
    return data.map((item: IUser) => ({
      createTime: moment(item.createTime, 'X').format('YYYY-MM-DD'),
      department: item.department,
      id: item.id,
      key: uuid.v4(),
      name: item.name,
      roleId: item.roleId,
      expireTime: item.expireTime ? moment(item.expireTime, 'X').format('YYYY-MM-DD') : '',
      validity: item.expireTime ?  `${moment(item.expireTime, 'X').diff(moment(), 'days') + 1}天` : '',
    }));
  } catch (e) {
    logger.error('getAllUsers err', e);
    return [];
  }
};

export const getAllRawUsers = async () => {
  try {
    const resp = await TRPCPost(`/${API_PREFIX}/trpc/trpc.rmonitor.user_manager.UserRoleAuth/GetAllUsers`, {
      productId: getProductId(),
    },  axiosRequestConfig());
    return resp.users;
  } catch (e) {
    logger.error('getAllRawUsers err', e);
    return [];
  }
};

// 添加用户
export const addUser = async (body: AddUserParams) => {
  try {
    const usernames = Array.isArray(body.name)
      ? body.name.map((name: string) => name.match(/[a-zA-Z_]*/g)?.filter((item: string) => !!item)?.[0])
      : [];
    const resp: IAddUserRsp = await TRPCPost(
      `/${API_PREFIX}/trpc/trpc.rmonitor.user_manager.UserRoleAuth/AddUser`,
      {
        productId: getProductId(),
        usernames,
        roleId: body.roleId,
        operator: getUserId(),
        platformId: getPid(),
        expireTime: body.expireTime,
      },
      {
        ...axiosRequestConfig(),
        ignoreError: true,
      },
    );
    const data = resp.baseRsp;
    return data;
  } catch (e) {
    logger.error('addUser err', e);
    return {};
  }
};

export const applyCreate = async (body: AddApplyUserParams) => {
  try {
    const resp: IAddUserRsp = await TRPCPost(
      `/${API_PREFIX}/trpc/trpc.rmonitor.user_manager.UserRoleAuth/ApplyCreate`,
      {
        productId: getProductId(),
        applyUserNames: body.name,
        roleId: body.roleId,
        applyer: body.applyer,
        platformId: getPid(),
        expireTime: body.expireTime,
        operator: getUserId(),
        userList: body.userList,
      },
      axiosRequestConfig(),
    );
    const data = resp.baseRsp;
    return data;
  } catch (e) {
    logger.error('addUser err', e);
    return {};
  }
};

export const setExpireTimeStrategie = async (body: AutoApplyParams) => {
  try {
    const resp: ISetStrategieRsp = await TRPCPost(
      `/${API_PREFIX}/trpc/trpc.rmonitor.user_manager.UserRoleAuth/SetExpireTimeStrategie`,
      {
        productId: getProductId(),
        operator: getUserId(),
        strategies: body.strategies,
        departments: body.departments,
      },
      axiosRequestConfig(),
    );
    const data = resp.baseRsp;
    return data;
  } catch (e) {
    logger.error('setStrategie err', e);
    return {};
  }
};

// 修改用户角色
export const changeUser = async (body: IChangeUserReq) => {
  try {
    const resp: IChangeUserRsp = await TRPCPost(
      `/${API_PREFIX}/trpc/trpc.rmonitor.user_manager.UserRoleAuth/ChangeUser`,
      {
        productId: getProductId(),
        operator: getUserId(),
        platformId: getPid(),
        ...body,
      },
      axiosRequestConfig(),
    );
    const data = resp.baseRsp;
    return data;
  } catch (e) {
    logger.error('changeUser err', e);
    return {};
  }
};

// 删除用户
export const deleteUser = async (body: IDeleteUserReq) => {
  try {
    const resp: IDeleteUserRsp = await TRPCPost(
      `/${API_PREFIX}/trpc/trpc.rmonitor.user_manager.UserRoleAuth/DeleteUser`,
      {
        productId: getProductId(),
        operator: getUserId(),
        platformId: getPid(),
        ...body,
      },
      axiosRequestConfig(),
    );
    const data = resp.baseRsp;
    return data;
  } catch (e) {
    logger.error('deleteUser err', e);
    return {};
  }
};


// CheckUserExist （批量）检查用户是否在用户列表内
export const CheckUserExist = async (usernames: string[]) => {
  try {
    const resp: ICheckUserExistRsp = await TRPCPost(`/${API_PREFIX}/trpc/trpc.rmonitor.user_manager.UserRoleAuth/CheckUserExist`, {
      usernames,
      productId: getProductId(),
      platformId: getPid(),
    });
    const data = resp.users ?? [];
    return data.map((item: IBasicUser) => ({
      id: item.role?.id,
      name: item.username,
      roleId: item.role?.name,
      expireTime: item.role?.expireTime ? moment(item.role?.expireTime, 'X').format('YYYY-MM-DD') : '',
    }));
  } catch (e) {
    logger.error('CheckUserExist err', e);
    return [];
  }
};
